import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { BrandMark } from "./Emblem";
import SocialLinks from "./SocialLinks";
import { COMPANY, CONTACT, LEGAL } from "../data/site";

const QUICK_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Fleet Ecosystem", to: "/fleet" },
  { label: "Goods Transport", to: "/goods-transport" },
  { label: "Passenger Transport", to: "/passenger-transport" },
  { label: "Our Gallery", to: "/gallery" },
  { label: "Upcoming Fleet", to: "/upcoming-fleet" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Why Choose Us", to: "/why-us" },
  { label: "Network", to: "/network" },
  { label: "Contact", to: "/contact" },
  { label: "Request a Quote", to: "/quote" },
];

const CORPORATE_ROWS = [
  ["CIN", LEGAL.cin],
  ["Status", LEGAL.status],
  ["ROC", LEGAL.roc],
  ["Incorporated", LEGAL.incorporated],
  ["Authorised Capital", LEGAL.authorisedCapital],
  ["Paid-up Capital", LEGAL.paidUpCapital],
  ["Operating Hours", CONTACT.hours],
];

export default function Footer() {
  return (
    <footer className="border-t border-gold/15 bg-surface" data-testid="footer">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-start gap-5">
              <BrandMark className="h-20 w-20 shrink-0" />
              <div>
                <p className="font-heading text-2xl font-semibold leading-snug tracking-[0.08em] text-foreground">
                  {COMPANY.name}
                </p>
                <p className="mt-3 font-heading text-base italic text-muted-foreground">“{COMPANY.tagline}”</p>
              </div>
            </div>
            <p className="mt-9 max-w-md text-base leading-[1.9] text-muted-foreground">
              A professional road transportation and logistics company, focused on dependable movement,
              responsible operations and long-term business relationships.
            </p>
            <SocialLinks className="mt-7" testIdPrefix="footer-social" />
          </div>

          <div className="lg:col-span-3">
            <p className="text-[13px] font-semibold uppercase tracking-[0.32em] text-gold">Quick Links</p>
            <ul className="mt-7 space-y-4">
              {QUICK_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-block text-base text-foreground/75 transition-all duration-300 hover:translate-x-1 hover:text-gold-light"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="text-[13px] font-semibold uppercase tracking-[0.32em] text-gold">Contact</p>
            <ul className="mt-7 space-y-5 text-base text-foreground/70">
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                <span className="leading-relaxed">{CONTACT.office}</span>
              </li>
              <li className="flex gap-3">
                <Phone className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                <a href={`tel:${CONTACT.phoneRaw}`} data-testid="footer-phone-link" className="transition-colors hover:text-gold-light">
                  {CONTACT.phoneDisplay}
                </a>
              </li>
              <li className="flex gap-3">
                <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="transition-colors hover:text-gold-light">
                  WhatsApp our team
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="break-words transition-colors hover:text-gold-light">
                  {CONTACT.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-20 border-t border-gold/10 pt-10">
          <p className="text-[13px] font-semibold uppercase tracking-[0.32em] text-gold">Corporate Information</p>
          <div className="mt-6 grid gap-x-10 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            {CORPORATE_ROWS.map(([k, v]) => (
              <span key={k}>
                <span className="font-semibold uppercase tracking-[0.14em] text-gold/80">{k}</span>
                <span className="mx-2 text-gold/60">·</span>
                <span className="tracking-wide text-foreground/70">{v}</span>
              </span>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-gold/10 pt-8 sm:flex-row sm:items-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} The Royal Mahaveer Roadlines Pvt Ltd. All rights reserved.
          </p>
          <div className="flex gap-8">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-sm text-muted-foreground transition-colors hover:text-gold-light">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid="footer-terms-link" className="text-sm text-muted-foreground transition-colors hover:text-gold-light">
              Terms &amp; Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
